// src/contexts/AppContext.jsx

import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

/**
 * Estados da aplicação
 */
export const AppStates = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  ERROR: 'error'
};

/**
 * Tipos de notificação
 * Mantidos para uso externo (ex: componentes que importam este enum),
 * mas o disparo visual agora é delegado ao NotificationContext.
 */
export const NotificationTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * Configurações de tema por tipo de usuário
 */
export const ThemeConfigs = {
  psicologo: {
    primary: '#3b82f6',
    secondary: '#22c55e',
    accent: '#fb923c'
  },
  supervisor: {
    primary: '#9333ea',
    secondary: '#2563eb',
    accent: '#22c55e'
  },
  instituicao: {
    primary: '#1f2937',
    secondary: '#3b82f6',
    accent: '#f97316'
  },
  internacional: {
    primary: '#06b6d4',
    secondary: '#a855f7',
    accent: '#f97316'
  },
  default: {
    primary: '#3b82f6',
    secondary: '#6b7280',
    accent: '#f97316'
  }
};

// Chave de namespace para preferências de UI no localStorage
// ⚠️  APENAS preferências de UI (tema, idioma, etc.)
// Sessão de autenticação é gerenciada exclusivamente pelo Supabase Auth
const UI_PREFS_KEY = 'psiando-ui-preferences';

/**
 * Estado inicial da aplicação
 *
 * CORREÇÃO BUG #4: removidos os campos de notificação in-app (notifications,
 * unreadCount) do estado do AppContext. O sistema de toasts/banners visuais
 * é responsabilidade exclusiva do NotificationContext
 * (NotificationContainer.jsx). Manter os dois causava:
 *   - showSuccess/showError duplicados que nunca se encontravam
 *   - Notificações despachadas pelo AppContext nunca aparecendo visualmente
 *   - Dois sistemas competindo pelo mesmo papel sem coordenação
 */
const initialState = {
  // Estado geral
  status: AppStates.INITIALIZING,
  isLoading: false,
  error: null,

  // Configurações de UI da aplicação (persistidas no localStorage)
  settings: {
    theme: 'default',
    language: 'pt-BR',
    timezone: 'America/Sao_Paulo',
    notifications: {
      email: true,
      push: true,
      sms: false
    },
    privacy: {
      profileVisible: true,
      showOnlineStatus: true,
      allowDirectMessages: true
    }
  },

  // Navegação e UI
  navigation: {
    sidebarOpen: false,
    activeSection: 'dashboard',
    breadcrumbs: []
  },

  // Cache de dados (invalidado a cada 30 minutos)
  cache: {
    supervisors: null,
    agendas: null,
    appointments: null,
    lastUpdated: null
  },

  // Configurações específicas por módulo
  modules: {
    agendaDigital: {
      enabled: false,
      configured: false,
      publicSlug: null
    },
    supervisao: {
      preferences: {
        frequency: 'quinzenal',
        modality: 'individual',
        preferredTimes: ['morning', 'afternoon']
      },
      matching: {
        autoSuggest: true,
        considerLocation: true,
        considerSpecialty: true
      }
    },
    community: {
      participationLevel: 'active',
      interests: []
    }
  },

  // Dados de conectividade
  network: {
    online: true,
    quality: 'good'
  }
};

/**
 * Reducer para gerenciar estado da aplicação
 */
function appReducer(state, action) {
  switch (action.type) {
    case 'SET_STATUS':
      return {
        ...state,
        status: action.payload,
        error: action.payload === AppStates.ERROR ? action.error : null
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload }
      };

    case 'SET_THEME':
      return {
        ...state,
        settings: { ...state.settings, theme: action.payload }
      };

    case 'UPDATE_NAVIGATION':
      return {
        ...state,
        navigation: { ...state.navigation, ...action.payload }
      };

    case 'SET_SIDEBAR_OPEN':
      return {
        ...state,
        navigation: { ...state.navigation, sidebarOpen: action.payload }
      };

    case 'SET_ACTIVE_SECTION':
      return {
        ...state,
        navigation: { ...state.navigation, activeSection: action.payload }
      };

    case 'UPDATE_BREADCRUMBS':
      return {
        ...state,
        navigation: { ...state.navigation, breadcrumbs: action.payload }
      };

    case 'UPDATE_CACHE':
      return {
        ...state,
        cache: { ...state.cache, ...action.payload, lastUpdated: new Date() }
      };

    case 'CLEAR_CACHE':
      return {
        ...state,
        cache: { supervisors: null, agendas: null, appointments: null, lastUpdated: null }
      };

    case 'UPDATE_MODULE_CONFIG':
      return {
        ...state,
        modules: {
          ...state.modules,
          [action.module]: { ...state.modules[action.module], ...action.payload }
        }
      };

    case 'SET_NETWORK_STATUS':
      return {
        ...state,
        network: { ...state.network, ...action.payload }
      };

    case 'RESET_APP_STATE':
      return { ...initialState, status: AppStates.READY };

    default:
      console.warn(`Ação não reconhecida no AppContext: ${action.type}`);
      return state;
  }
}

/**
 * Contexto da aplicação
 */
const AppContext = createContext();

/**
 * Hook para acessar o contexto da aplicação
 */
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp deve ser usado dentro de AppProvider');
  }
  return context;
};

/**
 * Provider do contexto da aplicação
 *
 * CORREÇÃO BUG #4: AppProvider não expõe mais showSuccess/showError/showWarning/
 * showInfo nem addNotification. Esses métodos pertencem ao NotificationContext
 * (useNotification hook). Componentes que precisam disparar toasts devem usar:
 *
 *   import { useNotification } from '@/components/ui/NotificationContainer';
 *   const { showSuccess, showError } = useNotification();
 *
 * O AppProvider continua responsável por: tema, configurações de UI, navegação,
 * cache, loading global e estado de rede — que são responsabilidades distintas.
 */
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Carregar preferências de UI salvas no localStorage
  const loadSavedSettings = useCallback(async () => {
    try {
      const saved = localStorage.getItem(UI_PREFS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
      }
    } catch (error) {
      console.error('Erro ao carregar preferências de UI:', error);
    }
  }, []);

  // Configurar listeners de conectividade de rede
  const setupNetworkListeners = useCallback(() => {
    const updateNetworkStatus = () => {
      dispatch({
        type: 'SET_NETWORK_STATUS',
        payload: {
          online: navigator.onLine,
          quality: navigator.connection?.effectiveType || 'unknown'
        }
      });
    };

    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Status inicial
    updateNetworkStatus();

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
    };
  }, []);

  // Inicialização da aplicação
  useEffect(() => {
    let cleanupNetworkListeners;

    const initializeApp = async () => {
      try {
        await loadSavedSettings();
        cleanupNetworkListeners = setupNetworkListeners();
        dispatch({ type: 'SET_STATUS', payload: AppStates.READY });
        console.log('🚀 AppProvider inicializado com sucesso');
      } catch (error) {
        console.error('Erro na inicialização do app:', error);
        dispatch({ type: 'SET_STATUS', payload: AppStates.ERROR, error: error.message });
      }
    };

    initializeApp();

    return () => {
      if (cleanupNetworkListeners) cleanupNetworkListeners();
    };
  }, [loadSavedSettings, setupNetworkListeners]);

  // Salvar preferências de UI no localStorage
  const saveSettings = useCallback((settings) => {
    try {
      const updatedSettings = { ...state.settings, ...settings };
      localStorage.setItem(UI_PREFS_KEY, JSON.stringify(updatedSettings));
      dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
    } catch (error) {
      console.error('Erro ao salvar preferências de UI:', error);
    }
  }, [state.settings]);

  // Gerenciar tema — aplica variáveis CSS e classe no body
  const setTheme = useCallback((themeName) => {
    const themeConfig = ThemeConfigs[themeName] || ThemeConfigs.default;

    const root = document.documentElement;
    Object.entries(themeConfig).forEach(([key, value]) => {
      root.style.setProperty(`--theme-${key}`, value);
    });

    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${themeName}`);

    dispatch({ type: 'SET_THEME', payload: themeName });
  }, []);

  // Navegação
  const setSidebarOpen = useCallback((open) => {
    dispatch({ type: 'SET_SIDEBAR_OPEN', payload: open });
  }, []);

  const setActiveSection = useCallback((section) => {
    dispatch({ type: 'SET_ACTIVE_SECTION', payload: section });
  }, []);

  const updateBreadcrumbs = useCallback((breadcrumbs) => {
    dispatch({ type: 'UPDATE_BREADCRUMBS', payload: breadcrumbs });
  }, []);

  // Loading global
  const setGlobalLoading = useCallback((loading) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  // Cache em memória (sem persistência — Supabase é a fonte de verdade)
  const updateCache = useCallback((data) => {
    dispatch({ type: 'UPDATE_CACHE', payload: data });
  }, []);

  const clearCache = useCallback(() => {
    dispatch({ type: 'CLEAR_CACHE' });
  }, []);

  // Verificar se cache é válido (30 minutos)
  const isCacheValid = useCallback((cacheKey) => {
    if (!state.cache.lastUpdated || !state.cache[cacheKey]) return false;
    const thirtyMinutes = 30 * 60 * 1000;
    const timeDiff = Date.now() - new Date(state.cache.lastUpdated).getTime();
    return timeDiff < thirtyMinutes;
  }, [state.cache]);

  const contextValue = {
    // Estado
    ...state,

    // Configurações
    saveSettings,
    setTheme,

    // Navegação
    setSidebarOpen,
    setActiveSection,
    updateBreadcrumbs,

    // Loading
    setGlobalLoading,

    // Cache
    updateCache,
    clearCache,
    isCacheValid,

    // Utilitários derivados
    isOnline: state.network.online,
    isReady: state.status === AppStates.READY,
    hasError: state.status === AppStates.ERROR
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
